import React from 'react';
import { PackageWithRelations } from '@/features/packages/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface PackageCardProps {
  package: PackageWithRelations;
  onEdit?: (pkg: PackageWithRelations) => void;
  onDelete?: (packageId: number) => void;
  showActions?: boolean;
}

export const PackageCard: React.FC<PackageCardProps> = ({
  package: pkg,
  onEdit,
  onDelete,
  showActions = true,
}) => {
  const typeColors: Record<string, string> = {
    standalone: 'bg-blue-100 text-blue-800',
    plan_bundle: 'bg-purple-100 text-purple-800',
    category_bundle: 'bg-green-100 text-green-800',
    mixed: 'bg-orange-100 text-orange-800',
  };

  const pricing = [];
  if (pkg.monthly_price) pricing.push(`$${pkg.monthly_price.toFixed(2)}/mo`);
  if (pkg.yearly_price) pricing.push(`$${pkg.yearly_price.toFixed(2)}/yr`);

  return (
    <Card className="relative flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="text-lg">{pkg.name}</CardTitle>
            {pkg.marketing_tagline && (
              <CardDescription className="mt-1 italic">{pkg.marketing_tagline}</CardDescription>
            )}
          </div>
          {showActions && (onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(pkg)}>
                    <Edit2 className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => {
                      if (confirm(`Delete package "${pkg.name}"?`)) {
                        onDelete(pkg.id);
                      }
                    }}
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="outline" className={typeColors[pkg.package_type] || ''}>
            {pkg.package_type.replace(/_/g, ' ')}
          </Badge>
          {pkg.is_featured && (
            <Badge className="bg-yellow-600">
              ⭐ Featured
            </Badge>
          )}
          {pkg.is_addon && <Badge variant="secondary">Add-on</Badge>}
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {/* Description */}
        {pkg.description && (
          <p className="text-sm text-gray-600">{pkg.description}</p>
        )}

        {/* Pricing */}
        {pricing.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-sm font-semibold text-gray-700">Pricing</p>
            <p className="text-sm text-gray-600">{pricing.join(' / ')}</p>
          </div>
        )}

        {/* Best For */}
        {pkg.best_for && (
          <div className="border-t pt-3">
            <p className="text-sm font-semibold text-gray-700">Best For</p>
            <p className="text-sm text-gray-600">{pkg.best_for}</p>
          </div>
        )}

        {/* Content Summary */}
        <div className="border-t pt-3">
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-2xl font-bold text-blue-600">{pkg.channel_count}</p>
              <p className="text-xs text-gray-600">Channel{pkg.channel_count !== 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{pkg.plan_count}</p>
              <p className="text-xs text-gray-600">Plan{pkg.plan_count !== 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{pkg.category_count}</p>
              <p className="text-xs text-gray-600">Categor{pkg.category_count !== 1 ? 'ies' : 'y'}</p>
            </div>
          </div>
        </div>

        {/* Plans List */}
        {pkg.plans.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">Included Plans</p>
            <div className="space-y-1">
              {pkg.plans.map((plan) => (
                <div key={plan.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{plan.name}</span>
                  {plan.monthly_price && (
                    <span className="text-xs text-gray-500">${plan.monthly_price}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories List */}
        {pkg.categories.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">Included Categories</p>
            <div className="space-y-1">
              {pkg.categories.map((cat) => (
                <div key={cat.id} className="text-sm text-gray-600">
                  {cat.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Channels Sample */}
        {pkg.channels.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">Sample Channels</p>
            <div className="space-y-1">
              {pkg.channels.slice(0, 3).map((channel) => (
                <div key={channel.id} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{channel.name}</span>
                  <span className="text-xs text-gray-400">
                    ({channel.source_type.replace(/_/g, ' ')})
                  </span>
                </div>
              ))}
              {pkg.channels.length > 3 && (
                <p className="text-xs text-gray-500">
                  + {pkg.channels.length - 3} more channel{pkg.channels.length - 3 !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PackageCard;
